import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import EventFormHeader from './components/EventFormHeader';
import BasicInfoSection from './components/BasicInfoSection';
import RichTextEditor from './components/RichTextEditor';
import LocationPicker from './components/LocationPicker';
import ImageUploadSection from './components/ImageUploadSection';
import PricingSection from './components/PricingSection';
import CapacitySection from './components/CapacitySection';
import RequirementsSection from './components/RequirementsSection';
import PublishSection from './components/PublishSection';
import { eventService } from '../../services/eventService';
import { validateTurkishContent } from '../../utils/turkishValidation';

const EventCreation = () => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    duration: '',
    difficulty: '',
    eventType: 'kitesurfing',
    city: 'istanbul',
    location: '',
    latitude: '',
    longitude: '',
    priceOwnBoard: '',
    priceWithRental: '',
    priceLifeVest: 0,
    priceSupMotor: 0,
    priceWaterproofBag: 0,
    maxParticipants: '',
    minParticipants: '',
    registrationDeadline: '',
    groupDiscount: false,
    earlyBirdDiscount: false,
    cancellationInsurance: false,
    waitingList: true,
    autoApprove: true,
    sendReminders: true,
    requiredEquipment: {},
    skillLevel: 'beginner',
    weatherConditions: {},
    customOptions: [],
    customOptionsType: 'multiple',
    allowOwnBoard: true,
    allowRentalBoard: true,
    allowStandardTicket: false,
    priceStandardTicket: 0,
    descOwnBoard: '',
    descRentalBoard: '',
    descStandardTicket: ''
  });
  
  const [images, setImages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Autocomplete Search Function
  const handleLocationSearch = (e) => {
    const value = e?.target?.value;
    // Update the form data for visual feedback
    setFormData(prev => ({ ...prev, location: value }));

    // Clear previous timeout (Debouncing)
    if (searchTimeoutRef?.current) clearTimeout(searchTimeoutRef?.current);

    if (value?.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Wait 500ms after typing stops, then fetch
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        // Use free-text search with q= parameter (no filters)
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&addressdetails=1&limit=5&countrycodes=tr`,
          {
            headers: {
              'User-Agent': 'PaddleTurkiye/1.0'
            }
          }
        );
        const data = await response?.json();
        
        // Debug: Log raw response to see what Nominatim returns
        console.log('Nominatim Raw Response:', data);
        
        // Display ALL results without filtering
        setSuggestions(data);
        setShowSuggestions(true);
      } catch (error) {
        console.error("Search error:", error);
      }
    }, 500);
  };

  // Handle Selection from Dropdown
  const selectLocation = (item) => {
    // Construct a shorter, cleaner location name
    // Priority: Suburb/Town > City/State
    const addr = item?.address || {};
    const district = addr?.suburb || addr?.town || addr?.village || addr?.county || item?.display_name?.split(',')?.[0];
    const city = addr?.city || addr?.province || addr?.state || '';
    
    // Format: "District, City" (e.g., "Alaçatı, İzmir" or "Turunç, Muğla")
    let shortName = district;
    if (city && !shortName?.includes(city)) {
      shortName += `, ${city}`;
    }
    
    // Fallback: If parsing fails, take first 2 parts of the long string
    if (shortName?.length < 3) {
      shortName = item?.display_name?.split(',')?.slice(0, 2)?.join(',');
    }
    
    setFormData(prev => ({
      ...prev,
      location: shortName, // Save the CLEAN name
      latitude: item?.lat,
      longitude: item?.lon
    }));
    
    // Clear suggestions
    setSuggestions([]);
    setShowSuggestions(false);
  };

  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    if (userRole !== 'admin') {
      navigate('/event-discovery');
    }
  }, [navigate]);

  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (formData?.title || formData?.description) {
        handleSaveDraft(true);
      }
    }, 60000);

    return () => clearInterval(autoSaveInterval);
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[name];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (name, value) => {
    if (name?.includes('.')) {
      const [parent, child] = name?.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev?.[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleImagesChange = (newImages) => {
    setImages(newImages);
    if (errors?.images) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.images;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.title?.trim()) {
      newErrors.title = 'Etkinlik başlığı gereklidir';
    } else {
      const titleError = validateTurkishContent(formData?.title, 'Etkinlik başlığı', 3);
      if (titleError) newErrors.title = titleError;
    }

    if (!formData?.description?.trim()) newErrors.description = 'Açıklama gereklidir';
    if (!formData?.time) newErrors.time = 'Saat gereklidir';
    if (!formData?.duration) newErrors.duration = 'Süre gereklidir';
    if (!formData?.difficulty) newErrors.difficulty = 'Zorluk seviyesi gereklidir';
    if (!formData?.eventType?.trim()) newErrors.eventType = 'Etkinlik türü gereklidir';
    if (!formData?.location?.trim()) newErrors.location = 'Konum gereklidir';
    if (!formData?.priceOwnBoard) newErrors.priceOwnBoard = 'Fiyat gereklidir';
    if (!formData?.priceWithRental) newErrors.priceWithRental = 'Kiralık fiyat gereklidir';
    if (!formData?.maxParticipants) newErrors.maxParticipants = 'Maksimum katılımcı gereklidir';
    if (!formData?.minParticipants) newErrors.minParticipants = 'Minimum katılımcı gereklidir';
    
    if (images?.length === 0) newErrors.images = 'En az bir görsel gereklidir';
    
    if (parseInt(formData?.minParticipants) > parseInt(formData?.maxParticipants)) {
      newErrors.minParticipants = 'Minimum katılımcı, maksimumdan fazla olamaz';
    }
    
    if (parseFloat(formData?.priceWithRental) <= parseFloat(formData?.priceOwnBoard)) {
      newErrors.priceWithRental = 'Kiralık fiyat, kendi tahtası fiyatından yüksek olmalı';
    }

    if (!formData?.date) {
      newErrors.date = 'Tarih gereklidir';
    } else {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      const eventDate = new Date(formData.date);
      if (isNaN(eventDate?.getTime())) {
        newErrors.date = 'Geçerli bir tarih giriniz (GG.AA.YYYY)';
      } else if (eventDate <= today) {
        newErrors.date = 'Etkinlik tarihi bugünden ileri bir tarih olmalıdır';
      }
    }

    if (!formData?.registrationDeadline) {
      newErrors.registrationDeadline = 'Kayıt son tarihi gereklidir';
    } else {
      const regDeadline = new Date(formData.registrationDeadline);
      const eventDate = new Date(formData.date);
      if (!isNaN(regDeadline?.getTime()) && !isNaN(eventDate?.getTime()) && regDeadline >= eventDate) {
        newErrors.registrationDeadline = 'Kayıt son tarihi, etkinlik tarihinden önce olmalıdır';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    if (!isAutoSave) {
      setIsSaving(true);
    }

    try {
      const eventData = {
        title: formData?.title,
        description: formData?.description,
        date: formData?.date,
        time: formData?.time,
        duration: formData?.duration,
        difficulty: formData?.difficulty,
        eventType: formData?.eventType,
        status: 'draft',
        location: formData?.location,
        city: formData?.city,
        latitude: formData?.latitude || null,
        longitude: formData?.longitude || null,
        priceOwnBoard: formData?.priceOwnBoard ? parseFloat(formData?.priceOwnBoard) : null,
        priceWithRental: formData?.priceWithRental ? parseFloat(formData?.priceWithRental) : null,
        maxParticipants: formData?.maxParticipants ? parseInt(formData?.maxParticipants) : null,
        minParticipants: formData?.minParticipants ? parseInt(formData?.minParticipants) : null,
        registrationDeadline: formData?.registrationDeadline,
        groupDiscount: formData?.groupDiscount,
        earlyBirdDiscount: formData?.earlyBirdDiscount,
        cancellationInsurance: formData?.cancellationInsurance,
        waitingList: formData?.waitingList,
        autoApprove: formData?.autoApprove,
        sendReminders: formData?.sendReminders,
        requiredEquipment: formData?.requiredEquipment,
        skillLevel: formData?.skillLevel,
        weatherConditions: formData?.weatherConditions,
        customOptions: formData?.customOptions,
        images: images?.map(img => img?.url)
      };

      await eventService?.create(eventData);

      const now = new Date();
      setLastSaved(now?.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }));

      if (!isAutoSave) {
        setTimeout(() => {
          setIsSaving(false);
        }, 1000);
      }
    } catch (error) {
      console.error('Taslak kaydedilemedi:', error);

      // Fallback to localStorage if Supabase fails
      const draftData = {
        ...formData,
        images: images?.map(img => img?.url),
        savedAt: new Date()?.toISOString(),
        status: 'draft'
      };
      localStorage.setItem('eventDraft', JSON.stringify(draftData));

      if (!isAutoSave) {
        setIsSaving(false);
      }
    }
  };

  const handlePreview = () => {
    const previewData = {
      ...formData,
      images: images?.map(img => img?.url),
      isPreview: true
    };
    localStorage.setItem('eventPreview', JSON.stringify(previewData));
    window.open('/event-details?preview=true', '_blank');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setSubmitError(null);

    try {
      const eventData = {
        title: formData?.title,
        description: formData?.description,
        date: formData?.date,
        time: formData?.time,
        duration: formData?.duration,
        difficulty: formData?.difficulty,
        eventType: formData?.eventType,
        status: 'published',
        location: formData?.location,
        city: formData?.city,
        latitude: formData?.latitude || null,
        longitude: formData?.longitude || null,
        priceOwnBoard: parseFloat(formData?.priceOwnBoard),
        priceWithRental: parseFloat(formData?.priceWithRental),
        priceLifeVest: parseFloat(formData?.priceLifeVest) || 0,
        priceSupMotor: parseFloat(formData?.priceSupMotor) || 0,
        priceWaterproofBag: parseFloat(formData?.priceWaterproofBag) || 0,
        maxParticipants: parseInt(formData?.maxParticipants),
        minParticipants: parseInt(formData?.minParticipants),
        registrationDeadline: formData?.registrationDeadline || null,
        groupDiscount: formData?.groupDiscount,
        earlyBirdDiscount: formData?.earlyBirdDiscount,
        cancellationInsurance: formData?.cancellationInsurance,
        waitingList: formData?.waitingList,
        autoApprove: formData?.autoApprove,
        sendReminders: formData?.sendReminders,
        requiredEquipment: formData?.requiredEquipment,
        skillLevel: formData?.skillLevel,
        weatherConditions: formData?.weatherConditions,
        customOptions: formData?.customOptions,
        customOptionsType: formData?.customOptionsType || 'multiple',
        images: formData?.images
      };

      const newEvent = await eventService?.create(eventData);
      
      // Success feedback
      alert('Etkinlik başarıyla oluşturuldu ve yayınlandı!');
      
      // Navigate to event details
      navigate(`/event-details/${newEvent?.id}`);
    } catch (error) {
      console.error('Error creating event:', error);
      setSubmitError(error?.message || 'Etkinlik oluşturulurken bir hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsPublishing(true);

    try {
      const eventData = {
        title: formData?.title,
        description: formData?.description,
        date: formData?.date,
        time: formData?.time,
        duration: formData?.duration,
        difficulty: formData?.difficulty,
        eventType: formData?.eventType,
        status: 'published',
        location: formData?.location,
        city: formData?.city,
        latitude: formData?.latitude || null,
        longitude: formData?.longitude || null,
        priceOwnBoard: parseFloat(formData?.priceOwnBoard),
        priceWithRental: parseFloat(formData?.priceWithRental),
        priceLifeVest: parseFloat(formData?.priceLifeVest) || 0,
        priceSupMotor: parseFloat(formData?.priceSupMotor) || 0,
        priceWaterproofBag: parseFloat(formData?.priceWaterproofBag) || 0,
        maxParticipants: parseInt(formData?.maxParticipants),
        minParticipants: parseInt(formData?.minParticipants),
        registrationDeadline: formData?.registrationDeadline || null,
        groupDiscount: formData?.groupDiscount,
        earlyBirdDiscount: formData?.earlyBirdDiscount,
        cancellationInsurance: formData?.cancellationInsurance,
        waitingList: formData?.waitingList,
        autoApprove: formData?.autoApprove,
        sendReminders: formData?.sendReminders,
        requiredEquipment: formData?.requiredEquipment,
        skillLevel: formData?.skillLevel,
        weatherConditions: formData?.weatherConditions,
        customOptions: formData?.customOptions,
        images: images?.map(img => img?.url)
      };

      const newEvent = await eventService?.create(eventData);

      // Clear localStorage draft
      localStorage.removeItem('eventDraft');

      setTimeout(() => {
        setIsPublishing(false);
        navigate('/event-discovery', {
          state: {
            message: 'Etkinliğiniz başarıyla yayınlandı!',
            eventId: newEvent?.id
          }
        });
      }, 1500);
    } catch (error) {
      console.error('Etkinlik yayınlanamadı:', error);
      setIsPublishing(false);
      setErrors({ publish: error?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="main-content">
        <EventFormHeader
          onSaveDraft={() => handleSaveDraft(false)}
          onPreview={handlePreview}
          isSaving={isSaving}
          lastSaved={lastSaved}
        />
        
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="space-y-6 md:space-y-8 lg:space-y-10">
            <BasicInfoSection
              formData={formData}
              errors={errors}
              onChange={handleInputChange}
            />
            
            <RichTextEditor
              value={formData?.description}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, description: value }));
                if (errors?.description) {
                  setErrors(prev => ({ ...prev, description: null }));
                }
              }}
              error={errors?.description}
            />
            
            <LocationPicker
              formData={formData}
              errors={errors}
              onChange={handleInputChange}
              suggestions={suggestions}
              showSuggestions={showSuggestions}
              onLocationSearch={handleLocationSearch}
              onSelectLocation={selectLocation}
            />
            
            <ImageUploadSection
              images={images}
              onImagesChange={handleImagesChange}
              error={errors?.images}
            />
            
            <PricingSection
              formData={formData}
              onChange={handleInputChange}
              onCheckboxChange={handleCheckboxChange}
            />
            
            <CapacitySection
              formData={formData}
              errors={errors}
              onChange={handleInputChange}
              onCheckboxChange={handleCheckboxChange}
            />
            
            <RequirementsSection
              formData={formData}
              onCheckboxChange={handleCheckboxChange}
            />
            
            <PublishSection
              onPublish={handlePublish}
              onSaveDraft={() => handleSaveDraft(false)}
              isPublishing={isPublishing}
              errors={errors}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCreation;